import { select, put, take, fork, takeEvery, cancel, takeLatest, spawn } from 'redux-saga/effects';
import { navigate } from 'domain/router/redux/reduxActions';
import { generatePath } from 'react-router-dom';
// eslint-disable-next-line import/no-cycle
import * as Env from 'domain/env/sagas';
import * as Router from 'domain/router';
import * as ACL from 'domain/restriction';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import { isMatchRoute } from 'domain/router/utils';
import { asyncErrorAction } from 'components/Form/validation';
import { expiredAction, cognitoAuthInitAction } from 'domain/env';
import { storage } from 'lib/storage';
import { notify } from 'lib/errorLogger';
// eslint-disable-next-line import/no-cycle
import { cognitoAuthHandler } from 'domain/env/sagas';

const PROHIBIDED_BACK_URL = ['login', 'resetpasswd', 'setpasswd', 'forgotPassword'];

// 24 hours * 60 min * 60 sec * 1000 ms
const DAY = 86400000;
const clearOldCacheDocuments = async () => {
  try {
    const cache = await caches.open('documents');
    if (cache) {
      cache.keys().then((keys) => {
        keys.forEach(async (request) => {
          const response = await cache.match(request);
          if (response) {
            const cacheAge = Date.parse(new Date()) - Date.parse(response.headers.get('date'));
            if (cacheAge > DAY) {
              cache.delete(request);
            }
          }
        });
      });
    }
  } catch (err) {
    notify.captureEvent(err);
  }
};

const getCurrentUrl = () => window.location.pathname + window.location.search;

const getCurrentRoute = () => Object.values(ROUTES_PATH).find((route) => isMatchRoute(route.absolute));
const getAllowedRoute = (restriction) => {
  const route = getCurrentRoute();
  return route ? ACL.check(route.restriction, restriction) : null;
};

const isProhibitedBackUrl = (route) => (route ? PROHIBIDED_BACK_URL.includes(route.id) : true);

function* watchSignIn() {
  function* signInFAILURE({ err }) {
    const { data } = err.response;
    yield put(asyncErrorAction('login', data));
    // remove token
    storage().token().remove();
    yield put(navigate.replace(generatePath(ROUTES_PATH.AUTH_LOGIN.absolute)));
  }
  const task = yield takeLatest(Env.action.signInAction.type, Env.ensureSignInWithUserProfile);
  yield takeEvery(Env.action.signInAction.failure, signInFAILURE);
  yield take(Env.action.signInAction.success);
  yield put(Router.completeEmptyAction('auth'));
  yield cancel(task);
  yield spawn(watchSignOut); // eslint-disable-line no-use-before-define
}

function* watchSignOut() {
  const task = yield takeEvery(Env.action.signOutAction.type, Env.ensureSignOut);
  // silent signout should triger watchSignin to allow for further signin
  yield takeLatest(Env.action.silentSignOutAction.success, watchSignIn);
  const { payload } = yield take(Env.action.signOutAction.success);
  clearOldCacheDocuments();
  if (!(window.location.pathname === generatePath(ROUTES_PATH.MAINTENANCE.absolute))) {
    // we set backUrl if we were not logged out by 403 in interceptor. In case of 403 we
    // should not set it as user will be redirected to backUrl and 403 indicates he has
    // no access so we are running into login logout cycle
    if (
      (payload && payload.reason === process.env.REACT_APP_ACCESS_DENIED_CODE) ||
      isProhibitedBackUrl(getCurrentRoute())
    ) {
      yield put(navigate.replace(generatePath(ROUTES_PATH.AUTH_LOGIN.absolute)));
    } else {
      // SSO, in case user if logged-out when expired token is used to get userProfile
      storage().backUrl().set(getCurrentUrl());
      yield put(
        navigate.replace(generatePath(ROUTES_PATH.AUTH_LOGIN.absolute), {
          backUrl: getCurrentUrl(),
        }),
      );
    }
  }
  yield cancel(task);
  yield spawn(watchSignIn);
}

function* watchExpired() {
  yield put(navigate.replace(generatePath(ROUTES_PATH.EXPIRED.absolute)));
}

export default function* ensureSign() {
  const isAuthorized = yield select(Env.selector.isAuthorizedSelector);
  clearOldCacheDocuments();
  yield takeLatest(expiredAction.type, watchExpired);
  // watches signIn event emited by amplify event Hub
  yield takeLatest(cognitoAuthInitAction.type, cognitoAuthHandler);
  if (isAuthorized) {
    yield fork(watchSignOut);
  } else {
    // fix for https://dokka-ai.atlassian.net/browse/DA-8672
    // problem: if there is a token without a role in the LS then we have a white screen permanently.
    // decision: keep this couple in a consistent state in the case when a user is hypothetically not authorized
    if (storage().token().get()) {
      storage().token().set('');
      const { href } = window.location;
      window.location.href = href;
    }

    const restriction = yield select(ACL.myRestriction);
    const allowedRoute = getAllowedRoute(restriction);

    if (!(allowedRoute && allowedRoute.id !== '404')) {
      // persist backurl for SSO
      const currentURL = getCurrentUrl();
      storage().backUrl().set(currentURL);
      yield put({ type: Env.action.savePersistedBackUrlAction.type, payload: currentURL });
      yield put(
        navigate.replace(generatePath(ROUTES_PATH.AUTH_LOGIN.absolute), {
          backUrl: currentURL,
        }),
      );
    }
    yield fork(watchSignIn);
  }
}
